import notifee from "@notifee/react-native"
import AsyncStorage from "@react-native-async-storage/async-storage"
import { useUser } from "capsule"
import { useCallback, useEffect, useRef } from "react"
import { AppState, AppStateStatus, Platform } from "react-native"

import { useConfig } from "../hooks/useConfig"

export const AUTO_LOGOUT_FLAG = "auto_logout_inactivity"
const BG_TIMESTAMP_KEY = "bg_timestamp"

const useAutoLogout = (isAuthenticated: boolean) => {
  const { logout, userData, user, initializing } = useUser()
  const config = useConfig()
  const autoLogoutDelayMinutes = config?.autoLogoutDelayMinutes

  const handleInactivityTimeout = useCallback(async () => {
    await AsyncStorage.setItem(AUTO_LOGOUT_FLAG, "1")
    if (userData?.role === "patient") {
      notifee.cancelTriggerNotifications()
    }
    logout()
  }, [logout, userData?.role])

  const handleInactivityTimeoutRef = useRef(handleInactivityTimeout)
  handleInactivityTimeoutRef.current = handleInactivityTimeout

  const lastKnownDelayRef = useRef(autoLogoutDelayMinutes)
  lastKnownDelayRef.current = autoLogoutDelayMinutes

  const checkPersistedTimestamp = useCallback(async () => {
    const delayMinutes = lastKnownDelayRef.current
    if (delayMinutes === undefined) {
      // Config pas encore chargée depuis Firestore : on ne peut pas évaluer le délai
      // de façon fiable. On laisse le timestamp en place pour le réévaluer une fois
      // la config disponible, plutôt que d'appliquer une valeur par défaut arbitraire.
      return
    }
    const stored = await AsyncStorage.getItem(BG_TIMESTAMP_KEY)
    if (!stored) {
      return
    }
    await AsyncStorage.removeItem(BG_TIMESTAMP_KEY)
    if (Date.now() - parseInt(stored, 10) >= delayMinutes * 60 * 1000) {
      await handleInactivityTimeoutRef.current()
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated || Platform.OS === "web") {
      return
    }
    checkPersistedTimestamp()
  }, [isAuthenticated, checkPersistedTimestamp, autoLogoutDelayMinutes])

  useEffect(() => {
    if (!isAuthenticated || Platform.OS === "web") {
      return
    }

    const handleAppStateChange = async (nextState: AppStateStatus) => {
      if (nextState === "background") {
        await AsyncStorage.setItem(BG_TIMESTAMP_KEY, Date.now().toString())
      } else if (nextState === "active") {
        await checkPersistedTimestamp()
      }
    }

    const subscription = AppState.addEventListener("change", handleAppStateChange)
    return () => subscription.remove()
  }, [isAuthenticated, checkPersistedTimestamp])

  useEffect(() => {
    if (!isAuthenticated || Platform.OS !== "web") {
      return
    }

    const webBgTime = { current: 0 }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        webBgTime.current = Date.now()
      } else if (webBgTime.current > 0) {
        const elapsed = Date.now() - webBgTime.current
        webBgTime.current = 0
        const delayMinutes = lastKnownDelayRef.current
        if (delayMinutes !== undefined && elapsed >= delayMinutes * 60 * 1000) {
          handleInactivityTimeoutRef.current()
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [isAuthenticated])

  const newTabCheckedRef = useRef(false)
  useEffect(() => {
    if (Platform.OS !== "web" || initializing || newTabCheckedRef.current) {
      return
    }
    newTabCheckedRef.current = true
    const isRefresh = Boolean(sessionStorage.getItem("tab_auth"))
    sessionStorage.setItem("tab_auth", "1")
    if (!isRefresh && user) {
      handleInactivityTimeoutRef.current()
    }
  }, [initializing, user])
}

export default useAutoLogout
